import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw, MailWarning, Users, Wrench } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface MismatchRow {
  user_id: string;
  full_name: string | null;
  auth_email: string | null;
  profile_email: string | null;
  profile_email_updated_at: string | null;
}

interface DuplicateRow {
  identity_key: string;
  match_count: number;
  auth_user_ids: string[];
  emails: (string | null)[];
  created_ats: string[];
  has_profile: boolean[];
  full_names: (string | null)[];
}

export function EmailMismatchAudit() {
  const mismatches = useQuery({
    queryKey: ["admin-email-mismatches"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_list_email_mismatches");
      if (error) throw error;
      return (data ?? []) as MismatchRow[];
    },
  });

  const duplicates = useQuery({
    queryKey: ["admin-duplicate-identities"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_list_possible_duplicate_identities");
      if (error) throw error;
      return (data ?? []) as DuplicateRow[];
    },
  });

  const refreshAll = () => {
    mismatches.refetch();
    duplicates.refetch();
  };
  const isFetching = mismatches.isFetching || duplicates.isFetching;

  const [reconciling, setReconciling] = useState(false);
  const runReconcile = async (dryRun: boolean) => {
    setReconciling(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-reconcile-email-mismatches", {
        body: { dryRun },
      });
      if (error) throw error;
      const d = data as { updated: number; failed: number; total: number; dry_run: boolean };
      toast.success(
        dryRun
          ? `Dry run: ${d.total} mismatch(es) would be reconciled`
          : `Reconciled ${d.updated} account(s); ${d.failed} failed`,
      );
      mismatches.refetch();
    } catch (e: unknown) {
      toast.error("Reconcile failed: " + (e as Error).message);
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="space-y-6">
      <EmailSyncLookup />

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MailWarning className="h-5 w-5" />
                Sign-in vs profile email mismatches
              </CardTitle>
              <CardDescription>
                Users whose auth sign-in email differs from the email stored on their profile. Sorted by most recent profile change.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => runReconcile(true)} disabled={reconciling || isFetching}>
                <Wrench className={`h-4 w-4 mr-1 ${reconciling ? "animate-pulse" : ""}`} />
                Dry run
              </Button>
              <Button size="sm" onClick={() => runReconcile(false)} disabled={reconciling || isFetching}>
                <Wrench className={`h-4 w-4 mr-1 ${reconciling ? "animate-pulse" : ""}`} />
                Reconcile all
              </Button>
              <Button variant="outline" size="sm" onClick={refreshAll} disabled={isFetching}>
                <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {mismatches.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : mismatches.error ? (
            <p className="text-sm text-destructive">Failed to load: {(mismatches.error as Error).message}</p>
          ) : !mismatches.data || mismatches.data.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No mismatches found. 🎉</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-3">
                {mismatches.data.length} user{mismatches.data.length === 1 ? "" : "s"} affected.
              </p>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Full name</TableHead>
                      <TableHead>Sign-in email (auth)</TableHead>
                      <TableHead>Profile email</TableHead>
                      <TableHead>Last profile change</TableHead>
                      <TableHead>User ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mismatches.data.map((r) => (
                      <TableRow key={r.user_id}>
                        <TableCell className="font-medium">{r.full_name ?? "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{r.auth_email ?? "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{r.profile_email ?? "—"}</TableCell>
                        <TableCell className="text-xs">
                          {r.profile_email_updated_at
                            ? format(new Date(r.profile_email_updated_at), "yyyy-MM-dd HH:mm")
                            : "—"}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{r.user_id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Possible duplicate identities
          </CardTitle>
          <CardDescription>
            Groups of sign-in accounts sharing the same full name — a signal that a "change email" flow may have created a new
            auth record instead of updating the existing one, or that the same person signed up twice.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {duplicates.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : duplicates.error ? (
            <p className="text-sm text-destructive">Failed to load: {(duplicates.error as Error).message}</p>
          ) : !duplicates.data || duplicates.data.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No duplicate identities detected.</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-3">
                {duplicates.data.length} name{duplicates.data.length === 1 ? "" : "s"} shared across multiple auth accounts.
              </p>
              <div className="space-y-4">
                {duplicates.data.map((group) => (
                  <div key={group.identity_key} className="rounded-md border p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="destructive">Possible duplicate identity</Badge>
                      <span className="font-medium">{group.full_names[0] ?? group.identity_key}</span>
                      <span className="text-xs text-muted-foreground">({group.match_count} accounts)</span>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Auth user ID</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead>Has profile row</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.auth_user_ids.map((id, i) => (
                            <TableRow key={id}>
                              <TableCell className="font-mono text-xs">{id}</TableCell>
                              <TableCell className="font-mono text-xs">{group.emails[i] ?? "—"}</TableCell>
                              <TableCell className="text-xs">
                                {group.created_ats[i]
                                  ? format(new Date(group.created_ats[i]), "yyyy-MM-dd HH:mm")
                                  : "—"}
                              </TableCell>
                              <TableCell>
                                {group.has_profile[i] ? (
                                  <Badge variant="outline">yes</Badge>
                                ) : (
                                  <Badge variant="secondary">no</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface LookupRow {
  user_id: string;
  full_name: string | null;
  auth_email: string | null;
  profile_email: string | null;
  in_sync: boolean;
}

function EmailSyncLookup() {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const { data, isFetching, error } = useQuery({
    queryKey: ["admin-email-sync-lookup", submitted],
    queryFn: async () => {
      if (!submitted) return [] as LookupRow[];
      const { data, error } = await supabase.rpc("admin_get_user_email_sync", { _query: submitted });
      if (error) throw error;
      return (data ?? []) as LookupRow[];
    },
    enabled: submitted.length > 0,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MailWarning className="h-5 w-5" />
          Per-user email sync check
        </CardTitle>
        <CardDescription>
          Look up a user by name, email, or user ID to see their sign-in email and profile email side-by-side with a match indicator.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(query.trim());
          }}
          className="flex gap-2 mb-4"
        >
          <input
            className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
            placeholder="Juliet Thompson, julietthompson84@gmail.com, or user ID"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button type="submit" size="sm" disabled={isFetching || !query.trim()}>
            {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
          </Button>
        </form>

        {error ? (
          <p className="text-sm text-destructive">Lookup failed: {(error as Error).message}</p>
        ) : submitted && data && data.length === 0 && !isFetching ? (
          <p className="text-sm text-muted-foreground">No matching user found.</p>
        ) : data && data.length > 0 ? (
          <div className="space-y-3">
            {data.map((r) => (
              <div key={r.user_id} className="rounded-md border p-3 flex items-start justify-between gap-4 flex-wrap">
                <div className="space-y-1 text-sm">
                  <div className="font-medium">{r.full_name ?? "(no name)"}</div>
                  <div className="font-mono text-xs text-muted-foreground">{r.user_id}</div>
                  <div className="text-xs">
                    <span className="text-muted-foreground">Sign-in email:</span>{" "}
                    <span className="font-mono">{r.auth_email ?? "—"}</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground">Profile email:</span>{" "}
                    <span className="font-mono">{r.profile_email ?? "—"}</span>
                  </div>
                </div>
                {r.in_sync ? (
                  <Badge className="bg-green-600 hover:bg-green-600 text-white">✓ In sync</Badge>
                ) : (
                  <Badge variant="destructive">✗ Mismatch</Badge>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

